// v4 ticket T4 (docs/product/spec-v4-review.md "Player + notes UI", spec-v4-extras.md A1/A6/A9/A11, spec-v4-director-
// plus.md `safe_zones`): reviewing a finished version. The client for T1's timeline, T2's notes and T3's previews,
// their shapes (the web doesn't import core, so they're copied and normalised here), and the pure helpers the review
// screen draws with: lanes, note pins and their grouping, status chips, what's on screen at a moment, the quoted yes,
// drawing marks, safe zones and before/after. Unit-tested in notes.test.ts.
//
// The source is swappable (installNotes): the mock (src/mock/notes.ts) replaces it, like characters.ts.
import { ApiError, call } from "./api";
import { fmtTime } from "./timeline";
import type { Aspect } from "./types";

// ------------------------------------------------------------------ shapes (api/src/v4/*.ts, core/src/timeline.ts)

export type TlScene = { n: number; sceneId: string | null; startS: number; endS: number; visual: string; onScreen: string; shotKind: string; characters: string[] };
export type TlLine = { id: string; speaker: string; speakerId: string; text: string; startS: number; endS: number; sceneN: number; approx?: boolean };
export type TlMusic = { startS: number; endS: number; assetId: string | null; label: string; ducked: boolean };
export type TlTransition = { atS: number; fromN: number; toN: number; style: string; lengthS: number };
export type TlSfx = { atS: number; endS?: number; sceneN: number; kind: "effect" | "ambience"; assetId: string | null; label: string };
export type TlCard = { kind: string; startS: number; endS: number; text: string };
export type Timeline = { durationS: number; look: string; scenes: TlScene[]; lines: TlLine[]; music: TlMusic[]; transitions: TlTransition[]; sfx: TlSfx[]; cards: TlCard[] };
export type VersionTimeline = { version: number; renderId: string; timeline: Timeline };

export const NOTE_KINDS = ["fix", "like", "question"] as const;
export type NoteKind = (typeof NOTE_KINDS)[number];
export const NOTE_PARTS = ["voice", "sfx", "music", "mix", "frame", "clip", "transition", "caption"] as const;
export type NotePart = (typeof NOTE_PARTS)[number];
export const NOTE_STATUSES = ["open", "planned", "approved", "working", "done", "skipped", "stale", "wontfix", "needs_spend"] as const;
export type NoteStatus = (typeof NOTE_STATUSES)[number];
/** Still in play (api LIVE_STATUSES). */
export const LIVE: NoteStatus[] = ["open", "planned", "approved", "needs_spend", "working"];

export type MarkType = "box" | "arrow" | "pen";
export type Pt = [number, number];
/** A1: drawn on one frame, 0..1 of the picture (box: two corners; arrow: from, to; pen: the stroke). */
export type Mark = { type: MarkType; points: Pt[]; atS?: number; label?: string };

export type PlanStep = { op: string; credits: number; label: string; free: boolean };
export type PlanOption = { label: string; steps: PlanStep[]; credits: number };
export type NotePlan = { steps: PlanStep[]; credits: number; summary: string; answer: string | null; by: string };
export type ThreadEntry = { by: "user" | "director" | "studio"; text: string; at: string };
export type NoteContext = {
  version: number | null;
  time: string;
  scene: { n: number; sceneId: string | null; visual: string; onScreen: string } | null;
  lines: { id: string; speaker: string; text: string; startS: number; endS: number }[];
  music: { label: string; ducked: boolean } | null;
  sfx: { label: string; atS: number }[];
  transition: { atS: number; fromN: number; toN: number; style: string } | null;
  /** a viewer's comment from a share link (V4-SH): outside words, shown as "From a viewer" */
  from: { kind: "share"; name: string } | null;
};
export type Pic = { assetId: string; url: string | null };
export type Note = {
  id: string;
  projectId: string;
  renderId: string;
  version: number | null;
  startS: number | null;
  endS: number | null;
  general: boolean;
  kind: NoteKind;
  about: "visual" | "audio" | "both";
  parts: NotePart[];
  body: string;
  context: NoteContext;
  plan: NotePlan | null;
  options: PlanOption[] | null;
  pick: number | null;
  status: NoteStatus;
  blockedReason: string | null;
  thread: ThreadEntry[];
  marks: Mark[];
  refs: Pic[];
  frame: Pic | null;
  crop: Pic | null;
  fixPreview: Pic | null;
  carriedFrom: string | null;
  createdAt: string;
  updatedAt: string;
};
export type NoteList = { version: number; renderId: string; notes: Note[] };
export type PreviewStatus = "queued" | "running" | "done" | "failed" | "cancelled";
export type PreviewClip = { assetId: string; url: string | null; seconds: number; leadInS: number; startS: number; endS: number };
export type Preview = { id: string; projectId: string; noteId: string | null; start: number; end: number; status: PreviewStatus; progress: number; error: string | null; clip: PreviewClip | null };
/** The SSE "preview" event (T3): progress, then done with the clip's url. */
export type PreviewEvent = { previewId: string; projectId: string; noteId: string | null; status: PreviewStatus; progress: number; error: string | null; url: string | null };

export type PlanResult = { note: Note; plan: NotePlan | null; quote: { credits: number; needsYes: boolean } | null; ask: string | null };
export type ApplyResult = { note: Note; status: NoteStatus; jobs: { id: string; kind: string; credits: number }[]; range: { startS: number; endS: number; approx: boolean } | null; needsSpend: { credits: number; was: number } | null; warnings: string[] };

// ------------------------------------------------------------------ normalising

type Json = Record<string, unknown>;
const isObj = (v: unknown): v is Json => !!v && typeof v === "object" && !Array.isArray(v);
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const s = (v: unknown, d = "") => (typeof v === "string" ? v : typeof v === "number" ? String(v) : d);
const sOrNull = (v: unknown) => (typeof v === "string" && v ? v : null);
const n = (v: unknown, d = 0) => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() && Number.isFinite(Number(v)) ? Number(v) : d);
const nOrNull = (v: unknown) => (v === null || v === undefined || v === "" ? null : Number.isFinite(Number(v)) ? Number(v) : null);
const oneOf = <T extends string>(v: unknown, list: readonly T[], d: T): T => (list.includes(v as T) ? (v as T) : d);
const pic = (v: unknown): Pic | null => (isObj(v) && s(v.assetId ?? v.asset_id) ? { assetId: s(v.assetId ?? v.asset_id), url: sOrNull(v.url) } : null);

export function normTimeline(v: unknown): Timeline {
  const o = isObj(v) ? v : {};
  return {
    durationS: n(o.durationS),
    look: s(o.look, "ad"),
    scenes: arr(o.scenes).filter(isObj).map((x) => ({ n: n(x.n), sceneId: sOrNull(x.sceneId), startS: n(x.startS), endS: n(x.endS), visual: s(x.visual), onScreen: s(x.onScreen), shotKind: s(x.shotKind, "still"), characters: arr(x.characters).map((c) => s(c)).filter(Boolean) })),
    lines: arr(o.lines).filter(isObj).map((x) => ({ id: s(x.id), speaker: s(x.speaker, "Narrator"), speakerId: s(x.speakerId, "narrator"), text: s(x.text), startS: n(x.startS), endS: n(x.endS), sceneN: n(x.sceneN), ...(x.approx ? { approx: true } : {}) })),
    music: arr(o.music).filter(isObj).map((x) => ({ startS: n(x.startS), endS: n(x.endS), assetId: sOrNull(x.assetId), label: s(x.label, "Music"), ducked: !!x.ducked })),
    transitions: arr(o.transitions).filter(isObj).map((x) => ({ atS: n(x.atS), fromN: n(x.fromN), toN: n(x.toN), style: s(x.style, "cut"), lengthS: n(x.lengthS) })),
    sfx: arr(o.sfx).filter(isObj).map((x) => ({ atS: n(x.atS), ...(x.endS !== undefined && x.endS !== null ? { endS: n(x.endS) } : {}), sceneN: n(x.sceneN), kind: x.kind === "ambience" ? "ambience" : "effect", assetId: sOrNull(x.assetId), label: s(x.label, "Sound") })),
    cards: arr(o.cards).filter(isObj).map((x) => ({ kind: s(x.kind), startS: n(x.startS), endS: n(x.endS), text: s(x.text) })),
  };
}

export function normVersionTimeline(v: unknown): VersionTimeline {
  const o = isObj(v) ? v : {};
  return { version: n(o.version, 1), renderId: s(o.renderId), timeline: normTimeline(o.timeline) };
}

const normMark = (v: unknown): Mark | null => {
  if (!isObj(v)) return null;
  const type = oneOf(v.type, ["box", "arrow", "pen"] as const, "pen");
  const points = arr(v.points).filter((p): p is unknown[] => Array.isArray(p) && p.length >= 2).map((p) => [clamp01(n(p[0])), clamp01(n(p[1]))] as Pt);
  if (!points.length) return null;
  return { type, points, ...(v.atS !== undefined && v.atS !== null ? { atS: n(v.atS) } : {}), ...(typeof v.label === "string" && v.label ? { label: v.label } : {}) };
};

const normStep = (x: unknown): PlanStep => {
  const o = isObj(x) ? x : {};
  return { op: s(o.op), credits: n(o.credits), label: s(o.label), free: o.free === undefined ? n(o.credits) === 0 : !!o.free };
};

function normPlan(v: unknown): NotePlan | null {
  if (!isObj(v)) return null;
  return { steps: arr(v.steps).map(normStep), credits: n(v.credits), summary: s(v.summary), answer: sOrNull(v.answer), by: s(v.by, "studio") };
}

function normContext(v: unknown): NoteContext {
  const o = isObj(v) ? v : {};
  const sc = isObj(o.scene) ? o.scene : null;
  const mu = isObj(o.music) ? o.music : null;
  const tr = isObj(o.transition) ? o.transition : null;
  const fr = isObj(o.from) && o.from.kind === "share" ? o.from : null;
  return {
    version: nOrNull(o.version),
    time: s(o.time),
    scene: sc ? { n: n(sc.n), sceneId: sOrNull(sc.sceneId), visual: s(sc.visual), onScreen: s(sc.onScreen) } : null,
    lines: arr(o.lines).filter(isObj).map((l) => ({ id: s(l.id), speaker: s(l.speaker, "Narrator"), text: s(l.text), startS: n(l.startS), endS: n(l.endS) })),
    music: mu ? { label: s(mu.label, "Music"), ducked: !!mu.ducked } : null,
    sfx: arr(o.sfx).filter(isObj).map((x) => ({ label: s(x.label, "Sound"), atS: n(x.atS) })),
    transition: tr ? { atS: n(tr.atS), fromN: n(tr.fromN), toN: n(tr.toN), style: s(tr.style, "cut") } : null,
    from: fr ? { kind: "share", name: s(fr.name).trim() } : null,
  };
}

export function normNote(v: unknown): Note {
  const o = isObj(v) ? v : {};
  const refs = arr(o.refs).map(pic).filter((x): x is Pic => !!x);
  return {
    id: s(o.id),
    projectId: s(o.projectId),
    renderId: s(o.renderId),
    version: nOrNull(o.version),
    startS: nOrNull(o.startS),
    endS: nOrNull(o.endS),
    general: !!o.general,
    kind: oneOf(o.kind, NOTE_KINDS, "fix"),
    about: oneOf(o.about, ["visual", "audio", "both"] as const, "both"),
    parts: arr(o.parts).filter((p): p is NotePart => NOTE_PARTS.includes(p as NotePart)),
    body: s(o.body),
    context: normContext(o.context),
    plan: normPlan(o.plan),
    options: Array.isArray(o.options) ? o.options.filter(isObj).map((x) => ({ label: s(x.label), steps: arr(x.steps).map(normStep), credits: n(x.credits) })) : null,
    pick: nOrNull(o.pick),
    status: oneOf(o.status, NOTE_STATUSES, "open"),
    blockedReason: sOrNull(o.blockedReason),
    thread: arr(o.thread).filter(isObj).map((t) => ({ by: oneOf(t.by, ["user", "director", "studio"] as const, "user"), text: s(t.text), at: s(t.at) })),
    marks: arr(o.marks).map(normMark).filter((m): m is Mark => !!m),
    refs: refs.length ? refs : arr(o.refAssetIds).map((id) => ({ assetId: s(id), url: null })),
    frame: pic(o.frame),
    crop: pic(o.crop),
    fixPreview: pic(o.fixPreview),
    carriedFrom: sOrNull(o.carriedFrom),
    createdAt: s(o.createdAt),
    updatedAt: s(o.updatedAt),
  };
}

export function normPreview(v: unknown): Preview {
  const o = isObj(v) ? (isObj(v.preview) ? v.preview : v) : {};
  const c = isObj(o.clip) ? o.clip : null;
  return {
    id: s(o.id),
    projectId: s(o.projectId),
    noteId: sOrNull(o.noteId),
    start: n(o.start),
    end: n(o.end),
    status: oneOf(o.status, ["queued", "running", "done", "failed", "cancelled"] as const, "queued"),
    progress: n(o.progress),
    error: sOrNull(o.error),
    clip: c && s(c.assetId) ? { assetId: s(c.assetId), url: sOrNull(c.url), seconds: n(c.seconds), leadInS: n(c.leadInS), startS: n(c.startS), endS: n(c.endS) } : null,
  };
}

/** The SSE "preview" event, or null when it isn't one. */
export function normPreviewEvent(v: unknown): PreviewEvent | null {
  if (!isObj(v) || !s(v.previewId)) return null;
  return { previewId: s(v.previewId), projectId: s(v.projectId), noteId: sOrNull(v.noteId), status: oneOf(v.status, ["queued", "running", "done", "failed", "cancelled"] as const, "queued"), progress: n(v.progress), error: sOrNull(v.error), url: sOrNull(v.url) };
}

function normPlanResult(v: unknown): PlanResult {
  const o = isObj(v) ? v : {};
  const q = isObj(o.quote) ? o.quote : null;
  return { note: normNote(o.note), plan: normPlan(o.plan), quote: q ? { credits: n(q.credits), needsYes: !!q.needsYes } : null, ask: sOrNull(o.ask) };
}

function normApply(v: unknown): ApplyResult {
  const o = isObj(v) ? v : {};
  const r = isObj(o.range) ? o.range : null;
  const ns = isObj(o.needsSpend) ? o.needsSpend : null;
  const note = normNote(o.note);
  return {
    note,
    status: oneOf(o.status, NOTE_STATUSES, note.status),
    jobs: arr(o.jobs).filter(isObj).map((j) => ({ id: s(j.id), kind: s(j.kind), credits: n(j.credits) })),
    range: r ? { startS: n(r.startS), endS: n(r.endS), approx: !!r.approx } : null,
    needsSpend: ns ? { credits: n(ns.credits), was: n(ns.was) } : null,
    warnings: arr(o.warnings).map((w) => s(w)).filter(Boolean),
  };
}

// ------------------------------------------------------------------ the source (http, or the mock)

/** A version: its number, or the latest. */
export type VersionRef = number | "latest";
export type NewNote = {
  body: string;
  start?: number;
  end?: number;
  kind: NoteKind;
  general?: boolean;
  parts?: NotePart[];
  marks?: Mark[];
  ref_asset_ids?: string[];
};
export type NotePatch = {
  body?: string;
  kind?: NoteKind;
  start?: number;
  end?: number;
  marks?: Mark[];
  ref_asset_ids?: string[];
  status?: "open" | "skipped" | "wontfix";
  reply?: { by: "user"; text: string };
};

export type NotesSource = {
  /** GET /projects/:id/versions/:v/timeline */
  timeline(projectId: string, v: VersionRef): Promise<VersionTimeline>;
  /** GET /projects/:id/versions/:v/notes */
  list(projectId: string, v: VersionRef): Promise<NoteList>;
  /** POST /projects/:id/versions/:v/notes */
  create(projectId: string, v: VersionRef, b: NewNote): Promise<Note>;
  /** GET /notes/:id */
  get(id: string): Promise<Note>;
  /** PATCH /notes/:id (409 STALE_NOTE when it changed since `updated_at`) */
  patch(id: string, b: NotePatch & { updated_at: string }): Promise<Note>;
  /** DELETE /notes/:id */
  remove(id: string): Promise<void>;
  /** POST /notes/:id/plan */
  plan(id: string, b?: { pick?: number }): Promise<PlanResult>;
  /** POST /notes/:id/approve (409 CONFIRM_REQUIRED for a price without the yes) */
  approve(id: string, b: { confirmed?: boolean; pick?: number }): Promise<Note>;
  /** POST /notes/:id/apply (409 LIKED unless override) */
  apply(id: string, b?: { override?: boolean }): Promise<ApplyResult>;
  /** POST /notes/:id/preview: the note's moment as the project is now (free) */
  previewNote(id: string, b?: { start?: number; end?: number; layers?: ("voice" | "music" | "sfx")[] }): Promise<Preview>;
  /** GET /projects/:id/previews?note= (newest first) */
  previews(projectId: string, noteId?: string): Promise<Preview[]>;
  /** GET /previews/:id */
  preview(id: string): Promise<Preview>;
};

const enc = encodeURIComponent;
const ver = (v: VersionRef) => (v === "latest" ? "latest" : String(v));

export const httpNotes: NotesSource = {
  async timeline(projectId, v) {
    return normVersionTimeline(await call("GET", `projects/${enc(projectId)}/versions/${ver(v)}/timeline`));
  },
  async list(projectId, v) {
    const o = await call<Json>("GET", `projects/${enc(projectId)}/versions/${ver(v)}/notes`);
    return { version: n(o?.version, 1), renderId: s(o?.renderId), notes: arr(o?.notes).map(normNote) };
  },
  async create(projectId, v, b) {
    const o = await call<Json>("POST", `projects/${enc(projectId)}/versions/${ver(v)}/notes`, b);
    return normNote(o?.note);
  },
  async get(id) {
    return normNote((await call<Json>("GET", `notes/${enc(id)}`))?.note);
  },
  async patch(id, b) {
    return normNote((await call<Json>("PATCH", `notes/${enc(id)}`, b))?.note);
  },
  async remove(id) {
    await call("DELETE", `notes/${enc(id)}`);
  },
  async plan(id, b = {}) {
    return normPlanResult(await call("POST", `notes/${enc(id)}/plan`, b));
  },
  async approve(id, b) {
    return normNote((await call<Json>("POST", `notes/${enc(id)}/approve`, b))?.note);
  },
  async apply(id, b = {}) {
    return normApply(await call("POST", `notes/${enc(id)}/apply`, b));
  },
  async previewNote(id, b = {}) {
    return normPreview(await call("POST", `notes/${enc(id)}/preview`, b));
  },
  async previews(projectId, noteId) {
    const o = await call<Json>("GET", `projects/${enc(projectId)}/previews${noteId ? `?note=${enc(noteId)}` : ""}`);
    return arr(o?.previews).map(normPreview);
  },
  async preview(id) {
    return normPreview(await call("GET", `previews/${enc(id)}`));
  },
};

let source: NotesSource = httpNotes;
export function installNotes(src: Partial<NotesSource>) {
  source = { ...source, ...src };
}
/** Always delegates to the installed source (http, or the mock under VITE_MOCK=1). */
export const notesApi: NotesSource = new Proxy({} as NotesSource, {
  get: (_t, k: keyof NotesSource) => {
    const v = source[k];
    return typeof v === "function" ? (v as (...a: unknown[]) => unknown).bind(source) : v;
  },
});

// ------------------------------------------------------------------ preview events (session.ts forwards SSE here)

const previewWatchers = new Set<(e: PreviewEvent) => void>();
export function onPreviewEvent(f: (e: PreviewEvent) => void): () => void {
  previewWatchers.add(f);
  return () => previewWatchers.delete(f);
}
export function firePreviewEvent(e: PreviewEvent) {
  for (const f of [...previewWatchers]) f(e);
}

/** A11: a PATCH refused as stale re-reads the note. An additive change (a reply) is sent again on the fresh copy;
 *  anything else comes back as `{note, stale: true}` so the screen shows the latest and the user decides. */
export async function patchFresh(src: Pick<NotesSource, "get" | "patch">, note: Pick<Note, "id" | "updatedAt">, b: NotePatch, opts: { retry?: boolean } = {}): Promise<{ note: Note; stale: boolean }> {
  try {
    return { note: await src.patch(note.id, { ...b, updated_at: note.updatedAt }), stale: false };
  } catch (e) {
    if (!(e instanceof ApiError) || e.code !== "STALE_NOTE") throw e;
    const d = isObj(e.details) && isObj(e.details.note) ? normNote(e.details.note) : await src.get(note.id);
    if (!opts.retry) return { note: d, stale: true };
    return { note: await src.patch(note.id, { ...b, updated_at: d.updatedAt }), stale: false };
  }
}

// ------------------------------------------------------------------ labels

/** What the user picks when adding a note (spec: change / like / question / general). */
export type NoteType = "change" | "like" | "question" | "general";
export const NOTE_TYPES: { type: NoteType; label: string; hint: string }[] = [
  { type: "change", label: "Change", hint: "Something to fix here" },
  { type: "like", label: "Like", hint: "Keep this as it is" },
  { type: "question", label: "Question", hint: "Ask about this moment" },
  { type: "general", label: "Whole video", hint: "A note on all of it" },
];
/** The api's kind + general flag for a type. */
export function noteTypeBody(t: NoteType): { kind: NoteKind; general: boolean } {
  if (t === "like") return { kind: "like", general: false };
  if (t === "question") return { kind: "question", general: false };
  return { kind: "fix", general: t === "general" };
}
export const typeOfNote = (x: Pick<Note, "kind" | "general">): NoteType => (x.general && x.kind === "fix" ? "general" : x.kind === "fix" ? "change" : x.kind);

export type Tone = "open" | "busy" | "ready" | "good" | "bad" | "quiet";
const STATUS: Record<NoteStatus, { label: string; tone: Tone }> = {
  open: { label: "Open", tone: "open" },
  planned: { label: "Plan ready", tone: "ready" },
  approved: { label: "Approved", tone: "ready" },
  working: { label: "Fixing", tone: "busy" },
  done: { label: "Done", tone: "good" },
  skipped: { label: "Skipped", tone: "quiet" },
  stale: { label: "Gone in this cut", tone: "quiet" },
  wontfix: { label: "Left as is", tone: "quiet" },
  needs_spend: { label: "Needs a yes", tone: "bad" },
};
/** The chip on a note: by status, with a like and a question read in their own words. */
export function statusChip(x: Pick<Note, "status" | "kind" | "blockedReason">): { label: string; tone: Tone } {
  if (x.kind === "like") return x.status === "skipped" || x.status === "stale" ? STATUS[x.status] : { label: "Keep", tone: "good" };
  if (x.kind === "question") return x.status === "done" ? { label: "Answered", tone: "good" } : x.status === "open" ? { label: "Asked", tone: "open" } : STATUS[x.status];
  if (x.status === "open" && x.blockedReason) return { label: "Needs you", tone: "bad" };
  return STATUS[x.status];
}

export const PART_LABEL: Record<NotePart, string> = { voice: "Voice", sfx: "Sound", music: "Music", mix: "Mix", frame: "Picture", clip: "Motion", transition: "Transition", caption: "Text" };

export const credits = (c: number) => `${Math.round(c)} credit${Math.round(c) === 1 ? "" : "s"}`;
/** "Free" or "About 9 credits". */
export const priceLabel = (c: number) => (c > 0 ? `About ${credits(c)}` : "Free");
/** The approve button: free plans just go; a priced one says its price. */
export const approveLabel = (c: number) => (c > 0 ? `Approve · ${credits(c)}` : "Approve (free)");
/** The quoted yes for a priced fix (spec-v4 quoted(): the user hears the price before the yes). */
export const yesLine = (c: number) => `That's about ${credits(c)}. Make the fix?`;

/** A short, quoted bit of a line. */
export function quoteOf(text: string, max = 48): string {
  const t = text.trim().replace(/\s+/g, " ");
  return t.length <= max ? t : `${t.slice(0, max - 1).replace(/[\s,.;:!?-]+$/, "")}…`;
}

/** "0:12.4", "0:12.4–0:15.0" or "Whole video". */
export function noteTime(x: Pick<Note, "general" | "startS" | "endS">): string {
  if (x.general || x.startS === null) return "Whole video";
  if (x.endS === null || x.endS - x.startS < 0.05) return fmtTime(x.startS);
  return `${fmtTime(x.startS)}–${fmtTime(x.endS)}`;
}

/** "Scene 4 · Maya: 'not today'" from a note's captured context. */
export function noteContextLine(x: Pick<Note, "general" | "context">): string {
  if (x.general || !x.context.scene) return "The whole video";
  const head = `Scene ${x.context.scene.n}`;
  const l = x.context.lines[0];
  if (l) return `${head} · ${l.speaker}: '${quoteOf(l.text)}'`;
  if (x.context.scene.onScreen) return `${head} · "${quoteOf(x.context.scene.onScreen)}"`;
  return x.context.scene.visual ? `${head} · ${quoteOf(x.context.scene.visual)}` : head;
}

// ------------------------------------------------------------------ what's on screen

/** The scene playing at `t` (the last one that started), or null before the first. */
export function sceneAt(tl: Pick<Timeline, "scenes">, t: number): TlScene | null {
  let hit: TlScene | null = null;
  for (const sc of tl.scenes) if (t >= sc.startS - 1e-6) hit = sc;
  return hit;
}
/** The line being said at `t` (a small grace either side). */
export function lineAt(tl: Pick<Timeline, "lines">, t: number): TlLine | null {
  return tl.lines.find((l) => l.startS - 0.15 <= t && t <= l.endS + 0.15) ?? null;
}
/** Under the player: "Scene 2 · Narrator: 'By nine, the good loaves…'", a card's text, or the scene's text. */
export function nowLine(tl: Timeline, t: number): string {
  const card = tl.cards.find((c) => c.startS <= t && t < c.endS);
  if (card && !tl.scenes.some((sc) => sc.startS <= t && t < sc.endS)) return card.kind === "intro" ? "Channel intro" : card.kind === "outro" ? "Channel outro" : card.text ? `"${quoteOf(card.text)}"` : "End card";
  const sc = sceneAt(tl, t);
  if (!sc) return "";
  const l = lineAt(tl, t);
  if (l) return `Scene ${sc.n} · ${l.speaker}: '${quoteOf(l.text)}'`;
  if (sc.onScreen) return `Scene ${sc.n} · "${quoteOf(sc.onScreen)}"`;
  return `Scene ${sc.n}`;
}

// ------------------------------------------------------------------ lanes, pins, grouping

export const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
export const clamp01 = (v: number) => clamp(v, 0, 1);
const r2 = (v: number) => Math.round(v * 100) / 100;

/** Percent along the timeline. */
export const pct = (t: number, durationS: number) => (durationS > 0 ? r2(clamp((t / durationS) * 100, 0, 100)) : 0);
/** A lane item's left + width in percent; never thinner than `minPct` (a point sound still shows). */
export function span(startS: number, endS: number, durationS: number, minPct = 0.6): { left: number; width: number } {
  const left = pct(startS, durationS);
  const width = Math.max(minPct, pct(Math.max(startS, endS), durationS) - left);
  return { left: r2(Math.min(left, 100 - minPct)), width: r2(Math.min(width, 100 - Math.min(left, 100 - minPct))) };
}

/** Packs overlapping items into rows (greedy, in start order): each item's row, and how many rows. */
export function stackRows<T extends { startS: number; endS: number }>(items: T[], gapS = 0.05): { rows: number[]; count: number } {
  const order = items.map((x, i) => ({ x, i })).sort((a, b) => a.x.startS - b.x.startS || a.i - b.i);
  const ends: number[] = [];
  const rows = new Array<number>(items.length).fill(0);
  for (const { x, i } of order) {
    let r = ends.findIndex((e) => e + gapS <= x.startS);
    if (r < 0) {
      r = ends.length;
      ends.push(x.endS);
    } else ends[r] = x.endS;
    rows[i] = r;
  }
  return { rows, count: ends.length };
}

export type PinGroup = { key: string; atS: number; x: number; notes: Note[] };
/** Note pins along a bar `widthPx` wide: pins closer than `minGapPx` share one group (shown with a count). Notes on
 *  the whole video have no pin. Sorted by time. */
export function pinGroups(notes: Note[], durationS: number, widthPx: number, minGapPx = 16): PinGroup[] {
  const timed = notes.filter((x) => !x.general && x.startS !== null).sort((a, b) => a.startS! - b.startS! || a.createdAt.localeCompare(b.createdAt));
  const out: PinGroup[] = [];
  for (const x of timed) {
    const px = durationS > 0 ? (clamp(x.startS!, 0, durationS) / durationS) * widthPx : 0;
    const last = out.at(-1);
    if (last && px - last.x < minGapPx) last.notes.push(x);
    else out.push({ key: x.id, atS: x.startS!, x: Math.round(px * 10) / 10, notes: [x] });
  }
  return out;
}

/** The notes panel's sections: what still needs something, the whole-video notes, then the closed ones. Each by time. */
export function groupNotes(notes: Note[]): { general: Note[]; live: Note[]; closed: Note[] } {
  const byTime = (a: Note, b: Note) => (a.startS ?? -1) - (b.startS ?? -1) || a.createdAt.localeCompare(b.createdAt);
  const isLive = (x: Note) => LIVE.includes(x.status) && !(x.kind === "like" && x.status === "open");
  const general = notes.filter((x) => x.general && isLive(x)).sort(byTime);
  const live = notes.filter((x) => !x.general && isLive(x)).sort(byTime);
  const closed = notes.filter((x) => !isLive(x)).sort(byTime);
  return { general, live, closed };
}
export const openCount = (notes: Note[]) => notes.filter((x) => x.kind !== "like" && LIVE.includes(x.status)).length;

/** A6: the note the playhead is in (its range, or a second after a point note), for "the list follows the playhead". */
export function noteAt(notes: Note[], t: number): Note | null {
  let best: Note | null = null;
  for (const x of notes) {
    if (x.general || x.startS === null) continue;
    const end = Math.max(x.endS ?? x.startS, x.startS + 1);
    if (x.startS - 0.1 <= t && t <= end && (!best || x.startS >= best.startS!)) best = x;
  }
  return best;
}

/** What a note can do next, by its kind and status. */
export function noteActions(x: Pick<Note, "kind" | "status" | "plan" | "general" | "startS">): { plan: boolean; approve: boolean; apply: boolean; skip: boolean; reopen: boolean; preview: boolean } {
  const fix = x.kind === "fix";
  return {
    plan: (fix && ["open", "planned", "needs_spend"].includes(x.status)) || (x.kind === "question" && x.status === "open"),
    approve: fix && !!x.plan && (x.status === "planned" || x.status === "needs_spend"),
    apply: fix && !!x.plan && x.status === "approved",
    skip: fix && ["open", "planned", "needs_spend", "approved"].includes(x.status),
    reopen: ["skipped", "wontfix"].includes(x.status),
    preview: fix && !x.general && x.startS !== null && (x.status === "working" || x.status === "done"),
  };
}

// ------------------------------------------------------------------ before / after (T3)

/** Before: the version from where the clip starts (start - leadIn) for the clip's length. After: the clip itself. */
export function beforeAfter(p: Pick<Preview, "start" | "clip">, versionUrl: string): { before: { url: string; start: number; end: number }; after: { url: string; start: number; end: number } } | null {
  if (!p.clip?.url) return null;
  const from = Math.max(0, p.start - p.clip.leadInS);
  const len = p.clip.seconds > 0 ? p.clip.seconds : p.clip.endS - p.clip.startS + p.clip.leadInS;
  return { before: { url: versionUrl, start: r2(from), end: r2(from + len) }, after: { url: p.clip.url, start: 0, end: r2(len) } };
}

/** A play of a note's moment: a second and a half of lead-in, a little after. */
export function momentRange(x: Pick<Note, "startS" | "endS">, durationS: number, lead = 1.5, tail = 1): { start: number; end: number } | null {
  if (x.startS === null) return null;
  const start = Math.max(0, x.startS - lead);
  const end = Math.min(durationS || Infinity, Math.max(x.endS ?? x.startS, x.startS + 0.5) + tail);
  return end > start ? { start: r2(start), end: r2(end) } : null;
}

// ------------------------------------------------------------------ transport

/** One frame forward or back (30 fps unless told), kept inside the video. */
export function stepFrame(t: number, dir: 1 | -1, durationS: number, fps = 30): number {
  const f = Math.round(t * fps) + dir;
  return Math.round(clamp(f / fps, 0, Math.max(0, durationS)) * 1000) / 1000;
}
export const SPEEDS = [0.25, 0.5, 1] as const;
export const nextSpeed = (cur: number) => SPEEDS[(SPEEDS.indexOf(cur as (typeof SPEEDS)[number]) + 1) % SPEEDS.length]!;

/** An In/Out range (A6 keys, phone "from here" / "to here"): ordered, inside the video, at least 0.1 s when set. */
export function setRange(cur: { start: number; end: number | null }, which: "in" | "out", t: number, durationS: number): { start: number; end: number | null } {
  const at = clamp(t, 0, durationS || t);
  if (which === "in") return { start: at, end: cur.end !== null && cur.end > at + 0.05 ? cur.end : null };
  if (at <= cur.start + 0.05) return { start: at, end: cur.start > at + 0.05 ? cur.start : null };
  return { start: cur.start, end: at };
}

/** Desktop zoom: keep the playhead inside the visible part of a wider track (scroll as little as possible). */
export function followScroll(playheadPct: number, viewPx: number, trackPx: number, scrollLeft: number, marginPx = 40): number {
  if (trackPx <= viewPx) return 0;
  const x = (playheadPct / 100) * trackPx;
  let next = scrollLeft;
  if (x < scrollLeft + marginPx) next = x - marginPx;
  else if (x > scrollLeft + viewPx - marginPx) next = x - viewPx + marginPx;
  return Math.round(clamp(next, 0, trackPx - viewPx));
}

// ------------------------------------------------------------------ drawing (A1)

/** A pointer position over the picture (client px) as 0..1 of the picture's box. */
export function toUnit(clientX: number, clientY: number, box: { left: number; top: number; width: number; height: number }): Pt {
  return [r4(clamp01((clientX - box.left) / (box.width || 1))), r4(clamp01((clientY - box.top) / (box.height || 1)))];
}
const r4 = (v: number) => Math.round(v * 10000) / 10000;

/** Thins a pen stroke to at most `max` points (the api takes 500), always keeping both ends. */
export function thin(points: Pt[], max = 200): Pt[] {
  if (points.length <= max) return points;
  const step = (points.length - 1) / (max - 1);
  return Array.from({ length: max }, (_, i) => points[Math.round(i * step)]!);
}

/** The finished mark from a drag, or null when it's too small to mean anything (a tap). */
export function finishMark(type: MarkType, points: Pt[], atS: number): Mark | null {
  if (!points.length) return null;
  const a = points[0]!;
  const b = points.at(-1)!;
  const far = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (type === "box") {
    if (Math.abs(b[0] - a[0]) < 0.02 || Math.abs(b[1] - a[1]) < 0.02) return null;
    return { type, points: [[Math.min(a[0], b[0]), Math.min(a[1], b[1])], [Math.max(a[0], b[0]), Math.max(a[1], b[1])]], atS: r4(atS) };
  }
  if (type === "arrow") return far < 0.03 ? null : { type, points: [a, b], atS: r4(atS) };
  if (points.length < 2 && far === 0) return null;
  return { type, points: thin(points), atS: r4(atS) };
}

/** An arrow's head: two short lines back from the tip (0..1 coords, drawn on a w x h box). */
export function arrowHead(from: Pt, to: Pt, w: number, h: number, sizePx = 14): [Pt, Pt] {
  const dx = (to[0] - from[0]) * w;
  const dy = (to[1] - from[1]) * h;
  const ang = Math.atan2(dy, dx);
  const p = (a: number): Pt => [(to[0] * w - sizePx * Math.cos(a)) / w, (to[1] * h - sizePx * Math.sin(a)) / h];
  return [p(ang - Math.PI / 7), p(ang + Math.PI / 7)];
}

// ------------------------------------------------------------------ safe zones (spec-v4-director-plus `safe_zones`)

export type Platform = "tiktok" | "reels" | "shorts" | "youtube" | "feed" | "title";
/** `transient`: only there some of the time (a player's controls while paused or touched, end cards at the end). */
export type Zone = { x: number; y: number; w: number; h: number; label: string; transient?: true };
export const PLATFORM_LABEL: Record<Platform, string> = { tiktok: "TikTok", reels: "Reels", shorts: "Shorts", youtube: "YouTube", feed: "Feed", title: "Title safe" };

/** The platforms worth checking for a shape. */
export function platformsFor(aspect: Aspect | string | null | undefined): Platform[] {
  if (aspect === "9:16") return ["tiktok", "reels", "shorts", "title"];
  if (aspect === "1:1") return ["feed", "title"];
  return ["youtube", "title"];
}

/**
 * Where each app's buttons and text sit over the video (approximate, 0..1 of the picture). Covered areas only.
 * The same table as core/src/safezones.ts SAFE_ZONES (the Director's safe_zones tool); the web doesn't import core,
 * so notes.test.ts checks the two are equal. Change both together.
 */
export const SAFE_ZONES: Record<Platform, Zone[]> = {
  tiktok: [
    { x: 0, y: 0, w: 1, h: 0.085, label: "Following · For You" },
    { x: 0.86, y: 0.38, w: 0.14, h: 0.47, label: "Like, comment, share" },
    { x: 0, y: 0.8, w: 0.82, h: 0.13, label: "Name and caption" },
    { x: 0, y: 0.93, w: 1, h: 0.07, label: "Tabs" },
  ],
  reels: [
    { x: 0, y: 0, w: 1, h: 0.075, label: "Reels header" },
    { x: 0.85, y: 0.5, w: 0.15, h: 0.38, label: "Like, comment, share" },
    { x: 0, y: 0.82, w: 0.84, h: 0.12, label: "Name and caption" },
    { x: 0, y: 0.94, w: 1, h: 0.06, label: "Tabs" },
  ],
  shorts: [
    { x: 0, y: 0, w: 1, h: 0.065, label: "Search · menu" },
    { x: 0.84, y: 0.45, w: 0.16, h: 0.4, label: "Like, comment, share" },
    { x: 0, y: 0.81, w: 0.83, h: 0.12, label: "Channel and title" },
    { x: 0, y: 0.93, w: 1, h: 0.07, label: "Tabs" },
  ],
  youtube: [
    { x: 0, y: 0, w: 1, h: 0.1, label: "Title", transient: true },
    { x: 0, y: 0.86, w: 1, h: 0.14, label: "Player controls", transient: true },
    { x: 0.72, y: 0.72, w: 0.28, h: 0.14, label: "End-screen cards", transient: true },
  ],
  feed: [
    { x: 0, y: 0.88, w: 0.55, h: 0.12, label: "Sound · tags" },
    { x: 0, y: 0, w: 1, h: 0.06, label: "Feed crop" },
  ],
  title: [],
};
/** "Title safe": the inner 90% (text) and 93% (action) of the picture. */
export const TITLE_SAFE = { text: 0.9, action: 0.93 };

/** Whether a box (0..1) sits under any of a platform's zones. */
export function covered(b: { x: number; y: number; w: number; h: number }, platform: Platform): Zone[] {
  if (platform === "title") {
    const m = (1 - TITLE_SAFE.text) / 2;
    return b.x < m || b.y < m || b.x + b.w > 1 - m || b.y + b.h > 1 - m ? [{ x: m, y: m, w: 1 - 2 * m, h: 1 - 2 * m, label: "Outside title safe" }] : [];
  }
  return SAFE_ZONES[platform].filter((z) => b.x < z.x + z.w && z.x < b.x + b.w && b.y < z.y + z.h && z.y < b.y + b.h);
}

// ------------------------------------------------------------------ deep links

/** /p/:id/review?v=2&t=12.4&note=… (T5: &safe=tiktok shows a platform's safe zones, &listen=1 listening mode) */
export function reviewPath(projectId: string, o: { v?: number | null; t?: number | null; note?: string | null; safe?: Platform | null; listen?: boolean | null } = {}): string {
  const q = new URLSearchParams();
  if (o.v) q.set("v", String(o.v));
  if (o.t !== undefined && o.t !== null && Number.isFinite(o.t)) q.set("t", String(Math.round(o.t * 10) / 10));
  if (o.note) q.set("note", o.note);
  if (o.safe && o.safe in PLATFORM_LABEL) q.set("safe", o.safe);
  if (o.listen) q.set("listen", "1");
  const qs = q.toString();
  return `/p/${enc(projectId)}/review${qs ? `?${qs}` : ""}`;
}

/** v4 (T5): the same media file? Signed links differ in their expiry and signature (a Director play of the version
 *  carries a fresh one), so compare the path without the query. */
export function sameMedia(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const strip = (u: string) => u.replace(/^https?:\/\/[^/]+/, "").split(/[?#]/)[0]!;
  return strip(a) === strip(b);
}

/** v4 (T5): what a Director tool call carries from the review screen: the video, the version in the url (null: the
 *  current one) and the playhead (add_note's "here"). Null anywhere else. */
export function reviewToolContext(pathname: string, search: string, t: number | null): { projectId: string; version: number | null; t: number | null } | null {
  const m = /^\/p\/([^/?#]+)\/review\/?$/.exec(pathname);
  if (!m) return null;
  const v = Number(new URLSearchParams(search).get("v"));
  return { projectId: decodeURIComponent(m[1]!), version: Number.isInteger(v) && v > 0 ? v : null, t: t !== null && Number.isFinite(t) && t >= 0 ? Math.round(t * 100) / 100 : null };
}

/** A ?safe= value, if it names a platform. */
export const platformParam = (v: string | null | undefined): Platform | null => (v && v in PLATFORM_LABEL ? (v as Platform) : null);

// ------------------------------------------------------------------ A9: listening mode + Watch later

/** A moment saved with "Later" while listening (or watching): kept per video on this device. */
export type Later = { t: number; version: number; label: string; at: string };
export const LATER_MAX = 20;
const laterKey = (projectId: string) => `cao.watchLater.${projectId}`;

/** Adds a moment (a second from one already saved replaces it), newest last, at most LATER_MAX. */
export function addLater(list: Later[], x: Later): Later[] {
  const rest = list.filter((l) => !(l.version === x.version && Math.abs(l.t - x.t) < 1));
  return [...rest, x].slice(-LATER_MAX);
}
export const dropLater = (list: Later[], x: Pick<Later, "t" | "version">) => list.filter((l) => !(l.version === x.version && l.t === x.t));

export function loadLater(projectId: string, store: Pick<Storage, "getItem"> | null = typeof localStorage !== "undefined" ? localStorage : null): Later[] {
  try {
    const v: unknown = JSON.parse(store?.getItem(laterKey(projectId)) ?? "[]");
    return arr(v).filter(isObj).map((x) => ({ t: n(x.t), version: n(x.version, 1), label: s(x.label), at: s(x.at) }));
  } catch {
    return [];
  }
}
export function saveLater(projectId: string, list: Later[], store: Pick<Storage, "setItem"> | null = typeof localStorage !== "undefined" ? localStorage : null) {
  try {
    store?.setItem(laterKey(projectId), JSON.stringify(list));
  } catch {
    /* private mode / full: the list just isn't kept */
  }
}

/** Listening mode: what the picture shows at `t`, said in a sentence (the scene's description). */
export function pictureLine(tl: Timeline, t: number): string {
  const sc = sceneAt(tl, t);
  if (!sc) return "";
  const what = sc.visual.trim().replace(/\s+/g, " ");
  const text = sc.onScreen ? ` The screen says "${quoteOf(sc.onScreen, 60)}".` : "";
  return `${what ? `${what.charAt(0).toUpperCase()}${what.slice(1)}`.replace(/[.!]?$/, ".") : `Scene ${sc.n}.`}${text}`;
}
